import React, { useState, useMemo } from 'react'
import { ChevronRight, ChevronDown } from 'lucide-react'
import './JsonTreeVisualizer.css'

interface TreeNodeProps {
  data: any
  label: string
  level: number
  isExpanded: boolean
  onToggle: () => void
  path: string
  collapsedNodes: Set<string>
  toggleNode: (path: string) => void
}

const TreeNode: React.FC<TreeNodeProps> = ({ data, label, level, isExpanded, onToggle, path, collapsedNodes, toggleNode }) => {
  const isObject = data !== null && typeof data === 'object' && !Array.isArray(data)
  const isArray = Array.isArray(data)
  const hasChildren = isObject || isArray
  const childrenCount = isObject ? Object.keys(data).length : isArray ? data.length : 0

  const renderValue = (value: any) => {
    if (value === null) return <span className="value-null">null</span>
    if (typeof value === 'string') return <span className="value-string">"{value}"</span>
    if (typeof value === 'number') return <span className="value-number">{value}</span>
    if (typeof value === 'boolean') return <span className="value-boolean">{value.toString()}</span>
    return null
  }

  const renderChildren = () => {
    if (!hasChildren || !isExpanded) return null

    if (isArray) {
      return data.map((item: any, index: number) => {
        const childPath = `${path}.${index}`
        return (
          <TreeNode
            key={index}
            data={item}
            label={index.toString()}
            level={level + 1}
            isExpanded={!collapsedNodes.has(childPath)}
            onToggle={() => toggleNode(childPath)}
            path={childPath}
            collapsedNodes={collapsedNodes}
            toggleNode={toggleNode}
          />
        )
      })
    }

    if (isObject) {
      return Object.entries(data).map(([key, value]) => {
        const childPath = `${path}.${key}`
        return (
          <TreeNode
            key={key}
            data={value}
            label={key}
            level={level + 1}
            isExpanded={!collapsedNodes.has(childPath)}
            onToggle={() => toggleNode(childPath)}
            path={childPath}
            collapsedNodes={collapsedNodes}
            toggleNode={toggleNode}
          />
        )
      })
    }

    return null
  }

  return (
    <div className="tree-node" style={{ paddingLeft: `${level * 20}px` }}>
      <div className="node-header" onClick={hasChildren ? onToggle : undefined}>
        {hasChildren && (
          <span className="expand-icon">
            {isExpanded ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
          </span>
        )}
        <span className="node-label">{label}</span>
        {hasChildren && (
          <span className="node-summary">
            {isArray ? '[]' : '{}'} {childrenCount} {isArray ? 'items' : 'keys'}
          </span>
        )}
        {!hasChildren && renderValue(data)}
      </div>
      {renderChildren()}
    </div>
  )
}

interface JsonTreeVisualizerProps {
  jsonData: any
  isVisible: boolean
  onClose: () => void
  isDarkMode?: boolean
}

const JsonTreeVisualizer: React.FC<JsonTreeVisualizerProps> = ({ jsonData, isVisible, onClose, isDarkMode = true }) => {
  const [collapsedNodes, setCollapsedNodes] = useState<Set<string>>(new Set())

  const toggleNode = (path: string) => {
    setCollapsedNodes(prev => {
      const next = new Set(prev)
      if (next.has(path)) {
        next.delete(path)
      } else {
        next.add(path)
      }
      return next
    })
  }

  const expandAll = () => {
    setCollapsedNodes(new Set())
  }

  const collapseAll = () => {
    const allPaths = new Set<string>()
    const collectPaths = (obj: any, currentPath: string = '') => {
      if (obj !== null && typeof obj === 'object') {
        if (Array.isArray(obj)) {
          obj.forEach((item, index) => {
            const path = currentPath ? `${currentPath}.${index}` : index.toString()
            allPaths.add(path)
            collectPaths(item, path)
          })
        } else {
          Object.keys(obj).forEach(key => {
            const path = currentPath ? `${currentPath}.${key}` : key
            allPaths.add(path)
            collectPaths(obj[key], path)
          })
        }
      }
    }
    collectPaths(jsonData)
    setCollapsedNodes(allPaths)
  }

  const isValidJson = useMemo(() => {
    try {
      return jsonData !== null && typeof jsonData === 'object'
    } catch {
      return false
    }
  }, [jsonData])

  if (!isVisible) return null

  return (
    <div className={`json-tree-visualizer ${!isDarkMode ? 'light-theme' : ''}`}>
      <div className="visualizer-content">
        {isValidJson ? (
          <div className="tree-container">
            {Array.isArray(jsonData) ? (
              jsonData.map((item, index) => {
                const nodePath = index.toString()
                return (
                  <TreeNode
                    key={index}
                    data={item}
                    label={index.toString()}
                    level={0}
                    isExpanded={!collapsedNodes.has(nodePath)}
                    onToggle={() => toggleNode(nodePath)}
                    path={nodePath}
                    collapsedNodes={collapsedNodes}
                    toggleNode={toggleNode}
                  />
                )
              })
            ) : (
              Object.entries(jsonData).map(([key, value]) => {
                const nodePath = key
                return (
                  <TreeNode
                    key={key}
                    data={value}
                    label={key}
                    level={0}
                    isExpanded={!collapsedNodes.has(nodePath)}
                    onToggle={() => toggleNode(nodePath)}
                    path={nodePath}
                    collapsedNodes={collapsedNodes}
                    toggleNode={toggleNode}
                  />
                )
              })
            )}
          </div>
        ) : (
          <div className="no-data">
            <p>No valid JSON data to display</p>
            <p>Please enter valid JSON in the editor</p>
          </div>
        )}
      </div>
    </div>
  )
}

export default JsonTreeVisualizer
